import React, { useState, useEffect, useCallback } from "react";
import { ProgressBar } from "react-bootstrap";
import api from "../services/api";
import { BackgroundJobs, BackgroundJob } from "../utils/backgroundJobs";
import { useLanguage } from "../i18n/LanguageContext";

const POLL_INTERVAL = 3000;

interface BackgroundJobsBarProps {
  onJobComplete?: (job: BackgroundJob) => void;
}

const JOB_ENDPOINT: Record<string, string> = {
  form106: "/form106/jobs",
  worklog: "/worklog/jobs",
};

const BackgroundJobsBar: React.FC<BackgroundJobsBarProps> = ({
  onJobComplete,
}) => {
  const [now, setNow] = useState(Date.now());

  // Tick every second so elapsed time stays fresh
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsed = (startedAt: number) => {
    const secs = Math.floor((now - startedAt) / 1000);
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  };
  const { t } = useLanguage();
  const [jobs, setJobs] = useState<BackgroundJob[]>(() =>
    BackgroundJobs.getAll(),
  );
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const refresh = useCallback(() => {
    setJobs(BackgroundJobs.getAll());
  }, []);

  // Poll all processing jobs
  useEffect(() => {
    const active = jobs.filter((j) => j.status === "processing");
    if (active.length === 0) return;

    const timer = setTimeout(async () => {
      for (const job of active) {
        try {
          const endpoint = JOB_ENDPOINT[job.type] || "/form106/jobs";
          const r = await api.get(`${endpoint}/${job.jobId}`);
          const { status, progress, result, error } = r.data;

          if (status === "done") {
            BackgroundJobs.update(job.jobId, {
              status: "done",
              result,
              progress,
            });
            onJobComplete?.({ ...job, status: "done", result });
          } else if (status === "failed") {
            BackgroundJobs.update(job.jobId, { status: "failed", error });
          } else if (progress) {
            BackgroundJobs.update(job.jobId, { progress });
          }
        } catch {}
      }
      refresh();
    }, POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [jobs, onJobComplete, refresh]);

  const visibleJobs = jobs.filter((j) => !dismissed.has(j.jobId));
  if (visibleJobs.length === 0) return null;

  const dismiss = (jobId: string) => {
    BackgroundJobs.remove(jobId);
    setDismissed((prev) => new Set(Array.from(prev).concat(jobId)));
    refresh();
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 9999,
        width: 320,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {visibleJobs.map((job) => {
        const pct =
          job.progress && job.progress.total > 0
            ? Math.round((job.progress.processed / job.progress.total) * 100)
            : null;

        return (
          <div
            key={job.jobId}
            style={{
              background: "#fff",
              border: `1px solid ${job.status === "failed" ? "#dc3545" : job.status === "done" ? "#198754" : "#0d6efd"}`,
              borderRadius: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              padding: "10px 12px",
              fontSize: 13,
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-1">
              <div style={{ fontWeight: 600, flex: 1, marginRight: 8 }}>
                {job.status === "processing" && "⏳ "}
                {job.status === "done" && "✅ "}
                {job.status === "failed" && "❌ "}
                {job.label}
              </div>
              <button
                onClick={() => dismiss(job.jobId)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: 0,
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {job.status === "processing" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {job.progress
                      ? `${t("pageOf")} ${job.progress.processed} ${t("of")} ${job.progress.total} ${t("pages")}`
                      : t("processingPages")}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color:
                        now - job.startedAt > 120000 ? "#dc3545" : "#6c757d",
                    }}
                  >
                    ⏱ {elapsed(job.startedAt)}
                  </div>
                </div>
                {pct !== null ? (
                  <ProgressBar
                    now={pct}
                    label={`${pct}%`}
                    animated
                    striped
                    style={{ height: 6 }}
                  />
                ) : (
                  <ProgressBar
                    animated
                    striped
                    now={100}
                    variant="info"
                    style={{ height: 6 }}
                  />
                )}
                {now - job.startedAt > 120000 && (
                  <div className="text-danger mt-1" style={{ fontSize: 10 }}>
                    ⚠️ Taking longer than expected — the server may still be
                    processing
                  </div>
                )}
              </>
            )}

            {job.status === "done" && job.result && (
              <div className="text-muted" style={{ fontSize: 11 }}>
                ✅ {job.result.successful} {t("successfulPages")}
                {job.result.failed > 0 &&
                  ` · ❌ ${job.result.failed} ${t("failedPages")}`}
                {" · "}
                {job.result.totalPages} {t("pages")}
              </div>
            )}

            {job.status === "failed" && (
              <div className="text-danger" style={{ fontSize: 11 }}>
                {job.error || t("uploadFailed")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BackgroundJobsBar;
